/* eslint-disable react/no-this-in-sfc */
import React, { useState, useContext } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { LoruleRequestConfig } from '@galaxy/lorule-react-commons';
import moment from 'moment-timezone';
import { UpdateActionMemoType } from '../../../api/actionMemoApiTypes';
import ConfigContext from '../../../contexts/ConfigContext';
import NinModal from '../../common/modal/NinModal';
import { sendToast, formatAmDisplayString } from '../../../utils/displayUtils';
import { updateActionMemo } from '../../../api/actionMemoApi';
import UpdateAmForm, { UpdateAmModalForm } from './AmUpdateForm';
import { AmTemplateType } from '../../../api/amTemplateApiTypes';
import {
    ACTION_REQUESTED_MAX_LENGTH,
    JUSTIFICATION_MAX_LENGTH,
    INITIAL_SELECT_ID,
    MINIMUM_COMPLETION_DAYS,
    MAXIMUM_COMPLETION_DAYS,
} from '../../../utils/Constants';

export interface UpdateAmFormBaseProps {
    title: String;
    toggleButton: JSX.Element;
    onSubmit?: () => void;
}

export interface UpdateAmModalProps extends UpdateAmFormBaseProps {
    id: number;
    amNumber: number;
    supNumber: number;
    confidential: boolean;
    subject: string;
    actionRequested: string;
    justification: string;
    creationDate: string;
    completionDate: string;
    budgetAmount: number;
    internalCostAmount: number;
    revenueAmount: number;
    nclDepartmentCode?: string | null;
    nclDepartmentDescription?: string | null;
    nclDevCode?: string | null;
    nclDevCodeDescription?: string | null;
    newCompletionDate?: string | null;
    template: AmTemplateType;
}

const submitUpdateAm = async (
    config: LoruleRequestConfig,
    values: UpdateAmModalForm,
    setResponseError: (errorMessage: string) => void,
    resetForm: () => void,
    setSubmitting: (isSubmitting: boolean) => void,
    toggleModal: () => void,
) => {
    try {
        const actionMemo: UpdateActionMemoType = {
            confidential: values.confidential,
            subject: values.subject,
            actionRequested: values.actionRequested ? values.actionRequested : '',
            justification: values.justification ? values.justification : '',
            completionDate: new Date(values.completionDate),
            budgetAmount: values.budgetAmount ? values.budgetAmount : 0,
            internalCostAmount: values.internalCostAmount ? values.internalCostAmount : 0,
            revenueAmount: values.revenueAmount ? values.revenueAmount : 0,
            nclDepartmentCode: values.nclDepartmentCode ? values.nclDepartmentCode : null,
            nclDevCode: values.nclDevCode ? values.nclDevCode : null,
            commentMessage: null,
        };
        await updateActionMemo(config, values.id, actionMemo);
        sendToast(
            <>
                Updated Action Memo:{' '}
                <strong>{formatAmDisplayString(values.amNumber, values.supNumber)}</strong>
            </>,
        );

        resetForm();
        toggleModal();

        return true;
    } catch (error) {
        if (error instanceof Error) {
            setResponseError(`Update failed:  ${error.message}`);
        }
        setSubmitting(false);
        return false;
    }
};

const AmUpdateModal = (props: UpdateAmModalProps) => {
    const config = useContext(ConfigContext);

    const [responseError, setResponseError] = useState('');

    const startDateMoment = moment();
    const completionDateMoment = moment(props.completionDate);
    const maxCompletionDateMoment = startDateMoment.add(props.template.completionDays, 'days');

    const isValidNewCompletionDate = (newCompletionDate: Date | undefined) => {
        if (newCompletionDate) {
            return true;
        }
        const newCompletionDateMoment = moment(newCompletionDate);
        return (
            newCompletionDateMoment.isSame(moment(props.completionDate)) ||
            newCompletionDateMoment.isSame(moment(props.newCompletionDate)) ||
            newCompletionDateMoment.isSame(maxCompletionDateMoment)
        );
    };

    const amUpdateValidationSchema = Yup.object().shape({
        subject: Yup.string()
            .required('Subject is required')
            // eslint-disable-next-line no-template-curly-in-string
            .max(250, 'Subject exceeds max length of ${max} characters')
            .matches(
                /^[\x20-\x7F]*$/,
                'Subject must consist of only ASCII characters (check for MS Word hyphenation)',
            ),
        actionRequested: Yup.string()
            .required('Action Requested is required')
            // eslint-disable-next-line no-template-curly-in-string
            .max(ACTION_REQUESTED_MAX_LENGTH, 'Subject exceeds max length of ${max} characters'),
        justification: Yup.string()
            .required('Justification is required')
            // eslint-disable-next-line no-template-curly-in-string
            .max(JUSTIFICATION_MAX_LENGTH, 'Justification exceeds max length of ${max} characters'),
        completionDate: Yup.date()
            .typeError('Completion Date is required')
            .test(
                'required-check',
                'Completion Date is required',
                (completionDate) => props.supNumber > 0 || !!completionDate,
            )
            .test(
                'after-check',
                'Completion Date must be after today',
                (completionDate) =>
                    isValidNewCompletionDate(completionDate) ||
                    moment(completionDate).isAfter(moment()),
            )
            .test(
                'after-one-month-check',
                'New Completion date must be 30 days after the creation date',
                (completionDate) =>
                    isValidNewCompletionDate(completionDate) ||
                    moment(completionDate).isAfter(
                        moment(props.creationDate).add(MINIMUM_COMPLETION_DAYS, 'days'),
                    ),
            )
            .test(
                'before-template-maximum',
                'Completion date must within the template maximum days',
                (completionDate) =>
                    isValidNewCompletionDate(completionDate) ||
                    moment(completionDate).isSameOrBefore(maxCompletionDateMoment),
            )
            .test(
                'before-decade-maximum',
                'Completion date must within 10 years',
                (completionDate) =>
                    isValidNewCompletionDate(completionDate) ||
                    moment(completionDate).isSameOrBefore(
                        moment(props.creationDate).add(MAXIMUM_COMPLETION_DAYS, 'days'),
                    ),
            ),
        budgetAmount: Yup.number()
            .typeError('Budget Amount must be a number')
            .required('Budget Amount is required')
            .test(
                'budget amount validation',
                'Budget Amount cannot be negative for original action memos',
                function test(budgetAmount) {
                    return this.parent.supNumber > 0 || !budgetAmount || budgetAmount >= 0;
                },
            ),
        internalCostAmount: Yup.number()
            .typeError('Internal Cost Amount must be a number')
            .required('Internal Cost Amount is required')
            .test(
                'internal cost amount validation',
                'Internal Cost Amount cannot be negative for original action memos',
                function test(internalCostAmount) {
                    return (
                        this.parent.supNumber > 0 || !internalCostAmount || internalCostAmount >= 0
                    );
                },
            ),
        revenueAmount: Yup.number()
            .typeError('Expected Revenue Amount must be a number')
            .required('Expected Revenue Amount is required'),
        nclDepartmentCode: Yup.string().test(
            'ncl department validation',
            'NCL Department is required',
            (nclDepartmentCode) =>
                props.supNumber > 0 || !props.template.showDevCode || !!nclDepartmentCode,
        ),
        nclDevCode: Yup.string().test(
            'ncl dev code validation',
            'NCL Dev Code is required',
            (nclDevCode) => props.supNumber > 0 || !props.template.showDevCode || !!nclDevCode,
        ),
    });

    const initialValues: UpdateAmModalForm = {
        id: props.id,
        amNumber: props.amNumber,
        supNumber: props.supNumber,
        subject: props.subject ? props.subject : '',
        confidential: props.confidential,
        confidentialFlag: props.confidential ? 1 : INITIAL_SELECT_ID,
        actionRequested: props.actionRequested ? props.actionRequested : '',
        justification: props.justification ? props.justification : '',
        completionDate:
            props.supNumber > 0 && props.newCompletionDate
                ? props.newCompletionDate
                : props.completionDate,
        budgetAmount: props.budgetAmount ? props.budgetAmount : 0,
        internalCostAmount: props.internalCostAmount ? props.internalCostAmount : 0,
        revenueAmount: props.revenueAmount ? props.revenueAmount : 0,
        nclDepartmentCode:
            props.supNumber <= 0 && props.template.showDevCode && props.nclDepartmentCode
                ? props.nclDepartmentCode
                : '',
        nclDepartmentDescription:
            props.supNumber <= 0 && props.template.showDevCode && props.nclDepartmentDescription
                ? props.nclDepartmentDescription
                : '',
        nclDevCode:
            props.supNumber <= 0 && props.template.showDevCode && props.nclDevCode
                ? props.nclDevCode
                : '',
        nclDevCodeDescription:
            props.supNumber <= 0 && props.template.showDevCode && props.nclDevCodeDescription
                ? props.nclDevCodeDescription
                : '',
    };

    return (
        <NinModal
            button={props.toggleButton}
            size="lg"
            render={(toggleModal: () => void) => (
                <Formik
                    initialValues={initialValues}
                    validationSchema={amUpdateValidationSchema}
                    onSubmit={async (values, { resetForm, setSubmitting }) => {
                        const submitSuccessful = await submitUpdateAm(
                            config,
                            values,
                            setResponseError,
                            resetForm,
                            setSubmitting,
                            toggleModal,
                        );
                        if (props.onSubmit && submitSuccessful) {
                            setResponseError('');
                            props.onSubmit();
                        }
                    }}
                    render={(formikProps) => (
                        <UpdateAmForm
                            {...formikProps}
                            completionDateMoment={completionDateMoment}
                            maxCompletionDateMoment={maxCompletionDateMoment}
                            template={props.template}
                            responseError={responseError}
                            setResponseError={setResponseError}
                            toggleModal={toggleModal}
                        />
                    )}
                />
            )}
        />
    );
};

export default AmUpdateModal;
